import Axios from "../utils/axiosInterceptor";
import { PATIENT_APIS } from "../utils/constants";
import { StatusCodes } from "http-status-codes";

export type DocumentViewUrlResponse = {
  documentId: number;
  viewUrl: string;
  fileName?: string;
  contentType?: string;
};

export const fetchDocumentViewUrl = async (
  documentId: number,
): Promise<DocumentViewUrlResponse> => {
  const response = await Axios.get(PATIENT_APIS.DOCUMENT_VIEW_URL(documentId));
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to load document");
};

export const downloadDocument = async (
  documentId: number,
  fallbackName?: string,
): Promise<void> => {
  const response = await Axios.get(PATIENT_APIS.DOCUMENT_DOWNLOAD(documentId), {
    responseType: "blob",
  });
  if (response.status !== StatusCodes.OK) {
    throw new Error("Unable to download document");
  }

  const disposition: string =
    response.headers?.["content-disposition"] ||
    response.headers?.["Content-Disposition"] ||
    "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match?.[1]
    ? decodeURIComponent(match[1])
    : fallbackName || `document-${documentId}`;

  const contentType =
    response.headers?.["content-type"] ||
    response.headers?.["Content-Type"] ||
    "application/octet-stream";

  const blobUrl = window.URL.createObjectURL(
    new Blob([response.data], { type: contentType }),
  );
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const fetchVisitDocumentViewUrl = async (
  visitId: number,
  kind: "idCard" | "geoTag",
): Promise<DocumentViewUrlResponse> => {
  const apiKind = kind === "idCard" ? "ID_CARD" : "GEO_TAG";
  const response = await Axios.get(
    PATIENT_APIS.VISIT_DOCUMENT_VIEW_URL(visitId, apiKind),
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to load document");
};

export const isVisitGeoTagUploaded = async (visitId: number): Promise<boolean> => {
  try {
    await fetchVisitDocumentViewUrl(visitId, "geoTag");
    return true;
  } catch {
    return false;
  }
};

export const deleteStageDocument = async (documentId: number): Promise<void> => {
  const response = await Axios.delete(PATIENT_APIS.DOCUMENT_DELETE(documentId));
  if (
    response.status === StatusCodes.OK ||
    response.status === StatusCodes.NO_CONTENT
  ) {
    return;
  }
  throw new Error("Unable to delete document");
};
